import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import React, {
	type FC,
	type PropsWithChildren,
	createContext,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";

import { CheckmarkOutlineIcon } from "@moment/icons";

import { Button } from "../Button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "../Dialog";
import { Input } from "../Input";
import { cn } from "../utils";

//created function here rather than importing as to not create a circular dependency
const notImplementedFn =
	<T,>(name: string, providerName: string, defaultReturn: T) =>
	() => {
		console.warn(
			`${name} not implemented. Make sure that you are calling this function within <${providerName} />`
		);
		return defaultReturn;
	};

export interface ConfirmDialogProps {
	headingTitle: string;
	headingIcon: React.ReactElement;
	content: string | React.ReactElement;
	challengeText?: string;
	variant: "normal" | "negative";
	confirmLabel: string;
	cancelLabel: string;
	preventDismiss?: boolean;
	// Simple mode removes the header and footer, leaving just the content
	simple?: boolean;
	fullButtons?: boolean;
	width?: "sm" | "normal";
}

const DEFAULT_PROPS: ConfirmDialogProps = {
	headingTitle: "Please confirm",
	headingIcon: <CheckmarkOutlineIcon />,
	content: "Are you sure you would like to continue?",
	challengeText: undefined,
	variant: "normal",
	confirmLabel: "Confirm",
	cancelLabel: "Cancel",
	preventDismiss: false,
	simple: false,
	fullButtons: false,
	width: "normal",
};

type ConfirmFn = (props?: Partial<ConfirmDialogProps>) => Promise<boolean>;
export type ConfirmDialogContextState = {
	confirm: ConfirmFn;
};

export const initialState: ConfirmDialogContextState = {
	confirm: () => {
		console.error(
			`confirm not implemented. Make sure that you are calling this function within <ConfirmDialogContext />`,
			{
				name: "confirm",
				providerName: "ConfirmDialogContext",
			}
		);
		return Promise.resolve(true);
	},
};

const ConfirmDialogContext = createContext<ConfirmDialogContextState>(initialState);

export const useConfirmDialog = () => useContext(ConfirmDialogContext);

export const ConfirmDialogWrapper: FC<PropsWithChildren> = ({ children }) => {
	// State
	const [visible, setVisible] = useState(false);
	const [props, setProps] = useState(DEFAULT_PROPS);
	// We can't set the resolve function directly in state because anonymous function do not trigger state updates
	const [resolve, setResolve] = useState<{
		fn: undefined | ((resolve: boolean | PromiseLike<boolean>) => void);
	}>({ fn: undefined });
	const [challengeInput, setChallengeInput] = useState("");

	// Methods
	const showDialog = useCallback(() => setVisible(true), [setVisible]);

	const closeDialog = useCallback(() => {
		setVisible(false);
		setChallengeInput("");
	}, [setVisible]);

	const confirm: ConfirmFn = useCallback(
		(props) => {
			const nonNullableProps = { ...props };
			Object.keys(nonNullableProps).forEach((key) =>
				!nonNullableProps[key] ? delete nonNullableProps[key] : {}
			);

			setProps({ ...DEFAULT_PROPS, ...nonNullableProps });
			showDialog();

			return new Promise<boolean>((resolve) => {
				setResolve({ fn: resolve });
			});
		},
		[setProps, showDialog, setResolve]
	);

	const onConfirm = useCallback(() => {
		resolve.fn?.(true);
		closeDialog();
	}, [resolve, closeDialog]);

	const onCancel = useCallback(() => {
		resolve.fn?.(false);
		closeDialog();
	}, [resolve, closeDialog]);

	const value = useMemo(() => ({ confirm }), [confirm]);

	const isChallengeValid = useMemo(() => {
		if (!props.challengeText) return true;
		return challengeInput === props.challengeText;
	}, [props.challengeText, challengeInput]);

	return (
		<ConfirmDialogContext.Provider value={value}>
			{children}
			<Dialog open={visible} onOpenChange={(open) => !open && onCancel()}>
				<DialogContent
					onInteractOutside={(event) => {
						if (props.preventDismiss) {
							event.preventDefault();
						}
					}}
					onEscapeKeyDown={(event) => {
						if (props.preventDismiss) {
							event.preventDefault();
						}
					}}
					hideCloseButton={props.preventDismiss}
					className={cn(props.width === "sm" && "max-w-sm")}
				>
					{props.simple && (
						<VisuallyHidden>
							<DialogTitle>Confirm</DialogTitle>
						</VisuallyHidden>
					)}
					{!props.simple && (
						<DialogHeader>
							<DialogTitle>{props.headingTitle}</DialogTitle>
						</DialogHeader>
					)}
					<DialogDescription>{props.content}</DialogDescription>
					{props.challengeText && (
						<div className="mt-0">
							<Input
								value={challengeInput}
								onChange={(e) => setChallengeInput(e.target.value)}
								placeholder={`Type "${props.challengeText}" to confirm`}
								autoFocus
							/>
						</div>
					)}
					{!props.simple && (
						<DialogFooter>
							<Button onClick={onCancel} aria-label="Cancel">
								{props.cancelLabel}
							</Button>
							{props.variant === "negative" && (
								<Button
									preset="destructive"
									onClick={onConfirm}
									aria-label="Confirm"
									disabled={!isChallengeValid}
								>
									{props.confirmLabel}
								</Button>
							)}
							{props.variant === "normal" && (
								<Button
									preset="primary"
									onClick={onConfirm}
									aria-label="Confirm"
									disabled={!isChallengeValid}
								>
									{props.confirmLabel}
								</Button>
							)}
						</DialogFooter>
					)}
					{props.fullButtons && (
						<div className="flex flex-col gap-1 mt-4">
							<Button
								preset="primary"
								onClick={onConfirm}
								disabled={!isChallengeValid}
							>
								{props.confirmLabel}
							</Button>
							<Button variant="text" onClick={onCancel}>
								{props.cancelLabel}
							</Button>
						</div>
					)}
				</DialogContent>
			</Dialog>
		</ConfirmDialogContext.Provider>
	);
};
